/**
 * Private session input data: stored in Bagless, never sent to Amplitude.
 * Amplitude only receives funnel step names + field keys.
 */

export const INPUT_VALIDATION = {
  NONE: 'none',
  REQUIRED: 'required',
  EMAIL: 'email',
  PHONE: 'phone',
  MIN_LENGTH: 'minLength',
};

const LOCAL_SESSION_KEY = 'bagless_amp_session_id';

export function slugifyFieldKey(raw) {
  let s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!s) s = 'input';
  if (/^[0-9]/.test(s)) s = `field_${s}`;
  return s.slice(0, 64);
}

export function getAmplitudeSessionId() {
  try {
    const id = window.amplitude?.getSessionId?.();
    if (id != null && id !== 0 && String(id) !== '0') return String(id);
  } catch (e) { /* Amplitude unavailable */ }
  try {
    let local = sessionStorage.getItem(LOCAL_SESSION_KEY);
    if (!local) {
      local = `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(LOCAL_SESSION_KEY, local);
    }
    return local;
  } catch (e) {
    return `local-${Date.now()}`;
  }
}

export function validateInputValue(value, validation = INPUT_VALIDATION.REQUIRED, minLength = 1) {
  const trimmed = String(value || '').trim();
  const type = String(validation || INPUT_VALIDATION.REQUIRED).trim() || INPUT_VALIDATION.REQUIRED;

  if (
    type === INPUT_VALIDATION.REQUIRED
    || type === INPUT_VALIDATION.EMAIL
    || type === INPUT_VALIDATION.PHONE
    || type === INPUT_VALIDATION.MIN_LENGTH
  ) {
    if (!trimmed) return { ok: false, error: 'required' };
  }

  if (type === INPUT_VALIDATION.NONE) {
    return { ok: true, value: trimmed };
  }

  if (type === INPUT_VALIDATION.EMAIL) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return { ok: false, error: 'email' };
    }
  }

  if (type === INPUT_VALIDATION.PHONE) {
    const digits = trimmed.replace(/\D+/g, '');
    if (digits.length < 7 || digits.length > 15) {
      return { ok: false, error: 'phone' };
    }
  }

  if (type === INPUT_VALIDATION.MIN_LENGTH) {
    const min = Math.max(1, Number(minLength) || 1);
    if (trimmed.length < min) return { ok: false, error: 'minLength' };
  }

  if (trimmed.length > 4000) return { ok: false, error: 'tooLong' };
  return { ok: true, value: trimmed };
}

export function parseToolArguments(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch (e) {
      return { value: raw };
    }
  }
  return {};
}

export function saveSessionInputData(model, payload, successCallback, errorCallback) {
  const fetcher = model?.worldFetcher;
  if (!fetcher?.saveInputFieldSessionData) {
    errorCallback?.(new Error('saveInputFieldSessionData unavailable'));
    return;
  }
  const worldId = model?.config?.worldId ?? model?.worldID;
  const amplitudeSessionId = payload.amplitudeSessionId || getAmplitudeSessionId();
  if (!worldId || !amplitudeSessionId) {
    errorCallback?.(new Error('worldId or amplitudeSessionId missing'));
    return;
  }
  fetcher.saveInputFieldSessionData(
    {
      worldId,
      objectId: payload.objectId || '',
      amplitudeSessionId,
      fieldKey: slugifyFieldKey(payload.fieldKey || 'input'),
      value: payload.value,
      source: payload.source === 'vapi' ? 'vapi' : 'user',
      funnelStep: payload.funnelStep || '',
      validation: payload.validation || INPUT_VALIDATION.REQUIRED,
      minLength: payload.minLength,
    },
    successCallback,
    errorCallback
  );
}
